import { isObservable, Observable } from "rxjs";

/**
 * Fire and forget - triggers async operations without waiting for completion.
 *
 * **IMPORTANT**: This function intentionally swallows ALL errors from the operation.
 * Only use this for non-critical background operations where failures can be safely ignored
 * (e.g., cache warming, analytics, prefetching).
 *
 * For critical operations, use proper error handling with try/catch or .catch() instead.
 *
 * @param operation - A thenable Promise or subscribable Observable to execute without awaiting
 *
 * @example
 * // With Promise - background cache warming (failures are acceptable)
 * fireAndForget(fetch('/api/prefetch-data'));
 *
 * @example
 * // With Observable - background data prefetch (failures are acceptable)
 * fireAndForget(madSDK.recipes.find(filters));
 */
export function fireAndForget<T>(
  operation: PromiseLike<T> | Observable<T>
): void {
  if (isObservable(operation)) {
    operation.subscribe({
      error: () => {}, // intentionally swallow errors for background operations
      complete: () => {} // no explicit unsubscribe needed - complete auto-unsubscribes
    });
  } else if (
    operation &&
    typeof (operation as PromiseLike<T>).then === "function"
  ) {
    // Use .then() instead of .catch() to ensure the promise chain is triggered
    (operation as PromiseLike<T>).then(
      () => {}, // success - do nothing
      () => {} // error - intentionally swallow for background operations
    );
  }
}
